import type {
  CommClientConfiguration,
  ICommClient,
  VideoCallMember,
  sessionEnded as sessionEndedEvent,
  SessionConnectFailedEvent,
  SessionErrorEvent,
  MuteChangedEvent,
  VideoStateChangedEvent,
  InitUcsOptions,
  BrowserLogOptions,
} from "@teladoc/ucs-client-react"
import { lazy } from "react"
import * as React from "react"
import * as ReactDOM from "react-dom"

window.React = React
window.ReactDOM = ReactDOM

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type UcsModule = typeof import("@teladoc/ucs-client-react")

const importUCS = (): Promise<UcsModule> =>
  importShim("@teladoc/ucs-client-react")

const importSpecificExport = <K extends keyof UcsModule>(exportName: K) =>
  importUCS().then((m) => m[exportName])

export const importSpecificExportMaybeUndefined = <T>(
  exportName: string
): Promise<T | undefined> =>
  importUCS().then(
    (m) => (m as unknown as Record<string, T | undefined>)[exportName]
  )
  
export const VideocallLayout = lazy(() =>
  importSpecificExport("VideocallLayout").then((d) => ({ default: d }))
)

export const initUCS = (
  configuration: CommClientConfiguration,
  options: InitUcsOptions
) =>
  importSpecificExport("initUCS").then((init) => init(configuration, options))

export const getSharedCommClient = () =>
  importSpecificExport("getSharedCommClient").then((f) => f())

export const setRtcClient = (commClient: ICommClient) =>
  importSpecificExport("setRtcClient").then((setRtcClient) =>
    setRtcClient(commClient)
  )

export const getRtcClient = () =>
  importSpecificExport("getRtcClient").then((f) => f())

export const PreCallTest = lazy(() =>
  importSpecificExport("PreCallTest").then((d) => ({ default: d }))
)

type UiCommSessionEndedCallback = Parameters<
  typeof sessionEndedEvent["watch"]
>[0]
// eslint-disable-next-line no-unused-vars
export const whenSessionEnded = (f: UiCommSessionEndedCallback) => {
  let unwatchSessionEnded: () => unknown = () => void 0
  const unwatch = () => {
    unwatchSessionEnded()
  }
  importSpecificExport("sessionEnded").then((sessionEnded) => {
    unwatchSessionEnded = sessionEnded.watch((uiCommSessionEndedArgs) =>
      f(uiCommSessionEndedArgs)
    )
  })
  return unwatch
}

export const whenSessionConnected = (f: () => void) => {
  let unwatchSessionConnected: () => unknown = () => void 0
  const unwatch = () => {
    unwatchSessionConnected()
  }
  importSpecificExport("sessionConnected").then((sessionConnected) => {
    unwatchSessionConnected = sessionConnected.watch(() => f())
  })
  return unwatch
}

export const whenSessionConnectFailed = (
  f: (event: SessionConnectFailedEvent) => void
) => {
  let unwatchSessionConnectFailed: () => unknown = () => void 0
  const unwatch = () => {
    unwatchSessionConnectFailed()
  }
  importSpecificExport("sessionConnectFailed").then((sessionConnectFailed) => {
    unwatchSessionConnectFailed = sessionConnectFailed.watch(f)
  })
  return unwatch
}

export const whenSessionError = (f: (event: SessionErrorEvent) => void) => {
  let unwatchSessionError: () => unknown = () => void 0
  const unwatch = () => {
    unwatchSessionError()
  }
  importSpecificExport("sessionError").then((sessionError) => {
    unwatchSessionError = sessionError.watch(f)
  })
  return unwatch
}

export const WaitingRoom = lazy(() =>
  importSpecificExport("WaitingRoom").then((d) => ({ default: d }))
)

export const UnsupportedWarning = lazy(() =>
  importSpecificExport("UnsupportedWarning").then((d) => ({ default: d }))
)

export const WarningBanner = lazy(() =>
  importSpecificExport("WarningBanner").then((d) => ({ default: d }))
)

export const isSupported = () =>
  importSpecificExport("isSupported").then((f) => f())

export const getControlButtonWhiteList = () =>
  importSpecificExport("getControlButtonWhiteList").then((f) => f())

export const setControlButtonWhiteList = (controls: string[]) =>
  importSpecificExport("setControlButtonWhiteList").then((f) => f(controls))

export const whenMembersUpdated = (f: (members: VideoCallMember[]) => void) => {
  let unwatchMembers: () => unknown = () => void 0
  const unwatch = () => {
    unwatchMembers()
  }
  importSpecificExport("membersStore").then((membersStore) => {
    unwatchMembers = membersStore.watch(f)
  })
  return unwatch
}

export const getMembersList = () =>
  importSpecificExport("membersStore").then((membersStore) =>
    membersStore.getState()
  )

export const setAudioVolume = (volume: number) =>
  importSpecificExport("setAudioVolume").then((f) => f(volume))

export const getAudioVolume = () =>
  importSpecificExport("getAudioVolume").then((f) => f())

export const getIsAudioMuted = () =>
  importSpecificExport("getIsAudioMuted").then((f) => f())

export const setIsAudioMuted = (muted: boolean) =>
  importSpecificExport("setIsAudioMuted").then((f) => f(muted))

export const getIsVideoDisabled = () =>
  importSpecificExport("getIsVideoDisabled").then((f) => f())

export const setIsVideoDisabled = (disabled: boolean) =>
  importSpecificExport("setIsVideoDisabled").then((f) => f(disabled))

export const whenMuteChanged = (f: (event: MuteChangedEvent) => void) => {
  let unwatchMuteChanged: () => unknown = () => void 0
  const unwatch = () => {
    unwatchMuteChanged()
  }
  importSpecificExport("muteChanged").then((muteChanged) => {
    unwatchMuteChanged = muteChanged.watch(f)
  })
  return unwatch
}

export const whenVideoStateChanged = (
  f: (event: VideoStateChangedEvent) => void
) => {
  let unwatchVideoStateChanged: () => unknown = () => void 0
  const unwatch = () => {
    unwatchVideoStateChanged()
  }
  importSpecificExport("videoStateChanged").then((videoStateChanged) => {
    unwatchVideoStateChanged = videoStateChanged.watch(f)
  })
  return unwatch
}

export const createLogEngineForBrowser = (options: BrowserLogOptions) =>
  importSpecificExport("createLogEngineForBrowser").then((f) => f(options))
